import { useEffect, useRef, useState } from "react";
import { usePoll } from "@/lib/usePoll";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, MessageCircle, Phone } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOnline } from "@/lib/connectivity";
import { minorToStr } from "@/lib/money";
import { usePrint } from "@/context/PrintContext";
import { SendInvoice } from "@/components/SendInvoice";

const BASE_WORD = { new: "New", accepted: "Accepted", ready: "Ready for pickup", out_for_delivery: "Out for delivery", completed: "Completed", cancelled: "Cancelled" };
const BASE_TABS = [["new", "New"], ["accepted,ready,out_for_delivery", "In progress"], ["completed,cancelled", "Done"]];
const WORD = BASE_WORD;
const R_WORD = { ...BASE_WORD, new: "Received", preparing: "Preparing", ready: "Ready", delivered: "Delivered", collected: "Collected" };
const R_TABS = [["new", "Received"], ["preparing,ready", "In progress"], ["delivered,collected,cancelled", "Done"]];
const isR = (s) => s?.business?.business_type === "restaurant";
const CHIP = { preparing: "bg-sky-100 text-sky-800", delivered: "bg-emerald-100 text-emerald-800", collected: "bg-emerald-100 text-emerald-800", new: "bg-amber-400 text-slate-950", accepted: "bg-sky-100 text-sky-800", ready: "bg-violet-100 text-violet-800", out_for_delivery: "bg-indigo-100 text-indigo-800", completed: "bg-emerald-100 text-emerald-800", cancelled: "bg-rose-100 text-rose-700" };
const errMsg = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong");

function chime() {
  try {
    const c = new (window.AudioContext || window.webkitAudioContext)();
    const o = c.createOscillator(); const g = c.createGain();
    o.frequency.value = 880; g.gain.setValueAtTime(0.08, c.currentTime); g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.6);
    o.connect(g).connect(c.destination); o.start(); o.stop(c.currentTime + 0.6);
  } catch { /* audio blocked */ }
}

export function useNewOrders() {
  const { session } = useAuth();
  const online = useOnline();
  const [n, setN] = useState(0);
  const last = useRef(null);
  const allowed = !!session?.permissions?.online_orders && !session?.offline;
  useEffect(() => {
    if (!allowed || !online) return undefined;
    const poll = () => api.get("/store/orders/new-count").then((r) => {
      if (last.current !== null && r.data.new > last.current) { toast.success("New online order"); chime(); }
      last.current = r.data.new; setN(r.data.new);
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 15000);
    return () => clearInterval(t);
  }, [allowed, online]);
  return n;
}

function StatusChip({ o }) {
  const { session } = useAuth();
  const WORD = isR(session) ? R_WORD : BASE_WORD;
  return <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${CHIP[o.status]}`} data-testid={`online-order-status-${o.number}`}>{WORD[o.status]}</span>;
}

function OrderCard({ o, reload }) {
  const online = useOnline();
  const { printSale } = usePrint();
  const { session } = useAuth();
  const can = (p) => !!session?.permissions?.[p];
  const rest = isR(session);
  const WORD = rest ? R_WORD : BASE_WORD;
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCancelling(false); setReason(""); }, [o.id]);
  const m = (n) => `${o.currency} ${minorToStr(n, o.decimals)}`;
  const act = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const status = (s) => act(() => api.post(`/store/orders/${o.id}/status`, { status: s, reason }), WORD[s]);
  const reprint = async () => {
    try { printSale((await api.post(`/sales/${o.sale_id}/reprint`, null, { headers: { "X-Branch-Id": o.branch_id } })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const complete = (method) => act(async () => { const r = await api.post(`/store/orders/${o.id}/complete`, { method }); toast.message(`Receipt ${r.data.sale.receipt_code}`); printSale(r.data.sale); }, "Order completed — sale recorded");
  const wa = `https://wa.me/${(o.customer.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${o.customer.name}, your order ${o.number} is ${WORD[o.status].toLowerCase()}. Track it: ${o.track_url}`)}`;
  const big = "flex h-12 flex-1 items-center justify-center rounded-full px-4 text-sm font-bold disabled:opacity-50";
  const quick = "flex h-11 items-center justify-center gap-1.5 rounded-full px-4 text-sm font-semibold";
  const open = rest ? o.status === "ready" : ["accepted", "ready", "out_for_delivery"].includes(o.status);
  const done = ["completed", "cancelled", "delivered", "collected"].includes(o.status);
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={`online-order-${o.number}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-base font-bold">{o.number}</span><StatusChip o={o} /></div>
          <div className="mt-1 text-xs text-slate-500">{o.method === "delivery" ? `Delivery${o.area ? ` · ${o.area}` : ""}` : "Pickup"} · {new Date(o.created_at).toLocaleString()}</div>
        </div>
        <span className="shrink-0 font-mono text-lg font-bold" data-testid={`online-order-total-${o.number}`}>{m(o.totals.total)}</span>
      </div>
      <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm">
        <div className="font-semibold">{o.customer.name}</div>
        <div className="break-words text-slate-600">{o.customer.phone}{o.address ? ` · ${o.address}` : ""}</div>
        {o.note && <div className="mt-1 text-xs italic text-slate-500">“{o.note}”</div>}
      </div>
      <ul className="mt-3 space-y-1 text-sm">
        {o.items.map((i) => <li key={i.product_id} className="flex justify-between gap-3"><span className="min-w-0">{i.qty} × {i.name}</span><span className="shrink-0 font-mono text-xs">{m(i.total)}</span></li>)}
        {o.totals.delivery_fee > 0 && <li className="flex justify-between gap-3 text-slate-500"><span>Delivery</span><span className="font-mono text-xs">{m(o.totals.delivery_fee)}</span></li>}
      </ul>
      {o.receipt_code && <div className="mt-2 text-xs font-semibold text-emerald-700" data-testid={`online-order-receipt-${o.number}`}>Sale {o.receipt_code} · {o.paid_by}</div>}
      {o.cancel_reason && <div className="mt-2 text-xs text-rose-600">Reason: {o.cancel_reason}</div>}
      {o.customer.phone && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <a href={`tel:${o.customer.phone}`} data-testid={`online-order-call-${o.number}`} className={`${quick} border`}><Phone className="h-4 w-4" />Call</a>
          <a href={wa} target="_blank" rel="noreferrer" data-testid={`online-order-whatsapp-${o.number}`} className={`${quick} bg-emerald-100 text-emerald-800`}><MessageCircle className="h-4 w-4" />WhatsApp</a>
        </div>)}
      <div className="mt-3 flex flex-wrap gap-2">
        {rest && o.status === "new" && <button disabled={busy} onClick={() => status("preparing")} data-testid={`online-order-prepare-${o.number}`} className={`${big} bg-slate-900 text-white`}>Start preparing</button>}
        {rest && o.status === "preparing" && <button disabled={busy} onClick={() => status("ready")} data-testid={`online-order-ready-${o.number}`} className={`${big} w-full basis-full border border-slate-300`}>Mark ready</button>}
        {!rest && o.status === "new" && <button disabled={busy} onClick={() => status("accepted")} data-testid={`online-order-accept-${o.number}`} className={`${big} bg-slate-900 text-white`}>Accept order</button>}
        {o.status === "accepted" && <button disabled={busy} onClick={() => status(o.method === "delivery" ? "out_for_delivery" : "ready")} data-testid={`online-order-advance-${o.number}`} className={`${big} w-full basis-full border border-slate-300`}>{o.method === "delivery" ? "Out for delivery" : "Ready for pickup"}</button>}
        {open && (online
          ? ["cash", "card"].map((pm) => <button key={pm} disabled={busy} onClick={() => complete(pm)} data-testid={`online-order-complete-${pm}-${o.number}`} className={`${big} bg-emerald-600 text-white`}>{rest ? (o.method === "delivery" ? "Delivered" : "Collected") : "Complete"} · {pm === "cash" ? "Cash" : "Card"}</button>)
          : <span className="flex h-12 flex-1 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-500" data-testid={`online-order-needs-internet-${o.number}`}>Needs internet</span>)}
        {done && o.sale_id && can("reprint") && <button onClick={reprint} data-testid={`online-order-reprint-${o.number}`} className={`${big} border`}>Reprint receipt</button>}
        {done && o.sale_id && <div className="w-full"><SendInvoice saleId={o.sale_id} branchId={o.branch_id} defaultPhone={o.customer.phone || ""} testid={`online-order-whatsapp-${o.number}`} /></div>}
      </div>
      {!done && (cancelling ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for cancelling" data-testid={`online-order-cancel-reason-${o.number}`} className="h-11 min-w-0 flex-1 rounded-full border px-4 text-base sm:text-sm" />
          <button disabled={busy || !reason.trim()} onClick={() => status("cancelled")} data-testid={`online-order-cancel-confirm-${o.number}`} className="h-11 rounded-full bg-rose-600 px-5 text-sm font-bold text-white disabled:opacity-50">Cancel order</button>
        </div>
      ) : <button onClick={() => setCancelling(true)} data-testid={`online-order-cancel-${o.number}`} className="mt-2 h-11 w-full rounded-full text-sm font-semibold text-rose-600 hover:bg-rose-50">Cancel order…</button>)}
    </article>
  );
}

function OrderRow({ o, active, onClick }) {
  const { session } = useAuth();
  const WORD = isR(session) ? R_WORD : BASE_WORD;
  return (
    <button onClick={onClick} data-testid={`online-order-row-${o.number}`} className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${active ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2"><span className="font-mono text-sm font-bold">{o.number}</span>{o.status === "new" && <span className="h-2 w-2 rounded-full bg-amber-400" />}</div>
        <div className={`truncate text-xs ${active ? "text-slate-300" : "text-slate-500"}`}>{o.customer.name} · {o.method === "delivery" ? "Delivery" : "Pickup"} · {WORD[o.status]}</div>
      </div>
      <span className="shrink-0 font-mono text-xs font-bold">{o.currency} {minorToStr(o.totals.total, o.decimals)}</span>
      <ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
    </button>
  );
}

export default function OnlineOrders() {
  const { session } = useAuth();
  const TABS = isR(session) ? R_TABS : BASE_TABS;
  const wide = useMediaQuery("(min-width: 1024px)");
  const [tab, setTab] = useState(TABS[0][0]);
  const [d, setD] = useState(null);
  const [selId, setSelId] = useState(null);
  const n = useNewOrders();
  const load = () => api.get("/store/orders", { params: { status: tab } }).then((r) => setD(r.data)).catch(() => setD({ items: [], new: 0 }));
  useEffect(() => { load(); }, [tab, n]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(load, 5000, [tab]);
  const items = d?.items || [];
  const sel = items.find((o) => o.id === selId) || items[0];
  return (
    <div className="h-full overflow-y-auto" data-testid="online-orders-page">
    <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6">
      <div className="flex flex-wrap items-center gap-x-3"><h1 className="font-display text-2xl font-bold">Online orders</h1>
        <Link to="/online-store" className="ml-auto flex h-11 items-center text-sm font-semibold text-emerald-700" data-testid="online-orders-store-link">Store settings →</Link></div>
      <div className="sticky top-0 z-10 -mx-3 mt-2 flex gap-2 overflow-x-auto bg-slate-50/95 px-3 py-2 backdrop-blur sm:mx-0 sm:px-0" data-testid="online-orders-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setSelId(null); }} data-testid={`online-orders-tab-${label.toLowerCase().replace(" ", "-")}`} className={`flex h-11 shrink-0 items-center rounded-full px-5 text-sm font-bold ${tab === k ? "bg-slate-900 text-white" : "border bg-white"}`}>
            {label}{k === "new" && d?.new ? <span className="ml-1.5 rounded-full bg-amber-400 px-1.5 text-[11px] text-slate-950" data-testid="online-orders-new-count">{d.new}</span> : null}</button>))}
      </div>
      {!d ? <p className="mt-6 text-sm text-slate-500">Loading…</p> : items.length === 0 ? <p className="py-16 text-center text-sm text-slate-500" data-testid="online-orders-empty">No orders here yet</p>
        : wide ? (
          <div className="mt-4 grid grid-cols-[340px_minmax(0,1fr)] gap-5" data-testid="online-orders-split">
            <div className="max-h-[calc(100dvh-180px)] space-y-1 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2" data-testid="online-orders-list">
              {items.map((o) => <OrderRow key={o.id} o={o} active={sel?.id === o.id} onClick={() => setSelId(o.id)} />)}
            </div>
            <div className="min-w-0" data-testid="online-order-detail">{sel && <OrderCard o={sel} reload={load} />}</div>
          </div>
        ) : <div className="mt-3 space-y-3 pb-6">{items.map((o) => <OrderCard key={o.id} o={o} reload={load} />)}</div>}
    </div>
    </div>
  );
}
